/**
 * Phones freeze background tabs and drop their requests; the request then
 * fails with a TypeError when the person comes back. These helpers wait for
 * the tab to be visible and online again before retrying, so a dropped
 * connection reads as a pause, not an error.
 */

/** fetch() rejects with a TypeError when the connection itself fails. */
export function isConnectionDrop(error: unknown): boolean {
  return (
    error instanceof TypeError ||
    (error as { code?: string } | null)?.code === "SCAN_INTERRUPTED"
  );
}

const abortError = () => new DOMException("Cancelled", "AbortError");

// When the tab last came back to the foreground. Requests that fail just after
// (before the phone's network is back) count as a dropped connection too.
let lastShownAt = -Infinity;
if (typeof document !== "undefined")
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") lastShownAt = Date.now();
  });
const RECENTLY_SHOWN_MS = 15000;
/** Test hook: forget when the tab was last shown. */
export function resetVisibilityTracking(): void {
  lastShownAt = -Infinity;
}

/** Hidden now, back within the last few seconds, or offline. */
export function connectionMayHaveDropped(hiddenDuringRequest = false): boolean {
  return (
    hiddenDuringRequest ||
    document.visibilityState !== "visible" ||
    Date.now() - lastShownAt < RECENTLY_SHOWN_MS ||
    navigator.onLine === false
  );
}

export function whenVisibleAndOnline(signal?: AbortSignal): Promise<void> {
  const ready = () =>
    document.visibilityState === "visible" && navigator.onLine !== false;
  if (signal?.aborted) return Promise.reject(abortError());
  if (ready()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const check = () => {
      if (!ready()) return;
      cleanup();
      resolve();
    };
    const cancel = () => {
      cleanup();
      reject(abortError());
    };
    const cleanup = () => {
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("online", check);
      signal?.removeEventListener("abort", cancel);
    };
    document.addEventListener("visibilitychange", check);
    window.addEventListener("online", check);
    signal?.addEventListener("abort", cancel, { once: true });
  });
}

export function pause(milliseconds: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      signal?.removeEventListener("abort", cancel);
      resolve();
    }, milliseconds);
    const cancel = () => {
      window.clearTimeout(timer);
      reject(abortError());
    };
    signal?.addEventListener("abort", cancel, { once: true });
  });
}

/** Track whether the tab was hidden at any point while something ran. */
export function watchVisibility(): { wasHidden: () => boolean; stop: () => void } {
  let hidden = document.visibilityState !== "visible";
  const watch = () => {
    if (document.visibilityState !== "visible") hidden = true;
  };
  document.addEventListener("visibilitychange", watch);
  return {
    wasHidden: () => hidden,
    stop: () => document.removeEventListener("visibilitychange", watch),
  };
}

/**
 * Run a request; if it dropped because the tab was backgrounded or offline,
 * wait until the tab is visible and online, give the network a moment to come
 * back, and try again. Failures on a tab that stayed in front are thrown at
 * once, as before, as is a drop that outlasts the retries.
 */
export async function retryAfterReconnect<T>(
  run: () => Promise<T>,
  signal?: AbortSignal,
  delays: number[] = [1500, 4000],
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    const visibility = watchVisibility();
    try {
      return await run();
    } catch (error) {
      if (
        !isConnectionDrop(error) ||
        !connectionMayHaveDropped(visibility.wasHidden()) ||
        attempt >= delays.length ||
        signal?.aborted
      )
        throw error;
    } finally {
      visibility.stop();
    }
    await whenVisibleAndOnline(signal);
    await pause(delays[attempt], signal);
  }
}
